import { RULE_TYPES, CASCADE_BEHAVIORS } from "../config/rules.config";
import {
  extractCascadeConfig,
  getAllPermissionKeysFromTree,
} from "./cascadeHelpers";
import { getTargetKey } from "./ruleTargetHelpers";

export function computePermissionState({
  formValues,
  rules,
  rolePresets,
  selectedRole,
  filteredPermissions,
  isRoleChange,
  prevLockedPermissions,
  prevThresholdParentStates,
}) {
  const allTreeKeys = getAllPermissionKeysFromTree(filteredPermissions);
  const values = {};
  allTreeKeys.forEach((key) => {
    values[key] = !!formValues[key];
  });

  if (isRoleChange) {
    applyRolePreset(
      values,
      selectedRole,
      rolePresets,
      filteredPermissions,
      allTreeKeys,
    );
  }

  const cascadeConfig = extractCascadeConfig(filteredPermissions, rules);
  const thresholdParentStates = applyCascadeSteadyState(
    values,
    cascadeConfig,
    prevThresholdParentStates || {},
  );

  const lockedPermissions = computeLockedPermissions(values, rules);
  const preserveOnReleaseKeys = getPreserveOnReleaseKeys(rules);

  if (prevLockedPermissions) {
    prevLockedPermissions.forEach((key) => {
      if (!lockedPermissions.has(key) && !preserveOnReleaseKeys.has(key)) {
        values[key] = false;
      }
    });
  }

  lockedPermissions.forEach((key) => {
    values[key] = true;
  });

  const disabledPermissions = computeDisabledPermissions(values, rules);

  return {
    nextValues: values,
    lockedPermissions,
    disabledPermissions,
    thresholdParentStates,
  };
}

function applyRolePreset(
  values,
  selectedRole,
  rolePresets,
  filteredPermissions,
  allTreeKeys,
) {
  allTreeKeys.forEach((key) => {
    values[key] = false;
  });

  if (!selectedRole || !rolePresets[selectedRole]) return;

  const preset = rolePresets[selectedRole];
  if (!preset.permissions || preset.permissions.length === 0) return;

  const templatesByPermission = {};
  filteredPermissions.forEach((section) => {
    (section.children || []).forEach((child) => {
      if (child.templates && child.templates.length > 0) {
        templatesByPermission[child.id] = child.templates.map((t) => t.id);
      }
    });
  });

  preset.permissions.forEach((permKey) => {
    values[permKey] = true;

    if (preset.autoCheckTemplates && templatesByPermission[permKey]) {
      templatesByPermission[permKey].forEach((tId) => {
        values[tId] = true;
      });
    }
  });
}

function applyCascadeSteadyState(values, cascadeConfig, prevThresholdParentStates) {
  const thresholdParentStates = {};

  cascadeConfig.forEach((group) => {
    const { parentKey, behavior, checkThreshold, children } = group;
    const directChildIds = children.map((c) => c.id);

    children.forEach((child) => {
      if (child.grandchildren && child.grandchildren.length > 0) {
        const childUpState = computeChildFromGrandchildren(
          behavior,
          child.grandchildren,
          values,
        );
        if (childUpState !== undefined) {
          applyChildGrandchildState(values, behavior, child, childUpState);
        }
      }
    });

    const prevState = prevThresholdParentStates[parentKey];
    const parentState = computeParentFromChildren(
      behavior,
      directChildIds,
      values,
      {
        checkThreshold,
        wasOn: !!prevState?.on,
        prevCheckedCount: prevState?.checkedCount,
      },
    );
    if (parentState !== undefined) {
      values[parentKey] = parentState;
    }

    if (behavior === CASCADE_BEHAVIORS.THRESHOLD_HYSTERESIS) {
      const checkedCount = directChildIds.filter((id) => !!values[id]).length;
      thresholdParentStates[parentKey] = { on: !!values[parentKey], checkedCount };
    }
  });

  return thresholdParentStates;
}

function computeChildFromGrandchildren(behavior, grandchildIds, values) {
  if (!grandchildIds || grandchildIds.length === 0) return undefined;

  switch (behavior) {
    case CASCADE_BEHAVIORS.STRICT_CASCADE:
      return grandchildIds.every((id) => !!values[id]);

    case CASCADE_BEHAVIORS.PARTIAL_CASCADE:
    case CASCADE_BEHAVIORS.NOTES_CASCADE:
    case CASCADE_BEHAVIORS.THRESHOLD_HYSTERESIS:
      return grandchildIds.some((id) => !!values[id]);

    case CASCADE_BEHAVIORS.CHILD_SELECTS_PARENT:
      return grandchildIds.some((id) => !!values[id]);

    case CASCADE_BEHAVIORS.LETTERS_RULE:
      return grandchildIds.some((id) => !!values[id]);

    case CASCADE_BEHAVIORS.MERCURY_INBOX_FINANCIALS_RULES:
    {
      const anyGrandchildChecked = grandchildIds.some((id) => !!values[id]);
      if (anyGrandchildChecked) return true;
      return undefined;
    }

    default:
      return undefined;
  }
}

function applyChildGrandchildState(values, behavior, child, derivedState) {
  switch (behavior) {
    case CASCADE_BEHAVIORS.STRICT_CASCADE:
      values[child.id] = derivedState;
      break;

    case CASCADE_BEHAVIORS.PARTIAL_CASCADE:
    case CASCADE_BEHAVIORS.THRESHOLD_HYSTERESIS:
      if (derivedState === true) {
        values[child.id] = true;
      } else {
        if (!values[child.id]) {
          values[child.id] = false;
        }
      }
      break;

    case CASCADE_BEHAVIORS.NOTES_CASCADE:
    case CASCADE_BEHAVIORS.CHILD_SELECTS_PARENT:
    case CASCADE_BEHAVIORS.LETTERS_RULE:
      values[child.id] = derivedState;
      break;

    case CASCADE_BEHAVIORS.MERCURY_INBOX_FINANCIALS_RULES:
      if (derivedState === true) {
        values[child.id] = true;
      }
      break;

    default:
      break;
  }
}

function computeParentFromChildren(behavior, childIds, values, meta = {}) {
  if (!childIds || childIds.length === 0) return undefined;

  switch (behavior) {
    case CASCADE_BEHAVIORS.STRICT_CASCADE:
    case CASCADE_BEHAVIORS.PARTIAL_CASCADE:
    case CASCADE_BEHAVIORS.NOTES_CASCADE:
      return childIds.every((id) => !!values[id]);

    case CASCADE_BEHAVIORS.CHILD_SELECTS_PARENT:
    case CASCADE_BEHAVIORS.LETTERS_RULE:
      return childIds.some((id) => !!values[id]);

    case CASCADE_BEHAVIORS.MERCURY_INBOX_FINANCIALS_RULES: {
      const anyChildChecked = childIds.some((id) => !!values[id]);
      if (anyChildChecked) return true;
      return undefined;
    }

    case CASCADE_BEHAVIORS.THRESHOLD_HYSTERESIS: {
      const checkedCount = childIds.filter((id) => !!values[id]).length;
      if (meta.wasOn) {
        const prevCount = meta.prevCheckedCount ?? checkedCount;
        return checkedCount >= prevCount;
      }
      return checkedCount >= (meta.checkThreshold ?? childIds.length);
    }

    default:
      return undefined;
  }
}

function computeLockedPermissions(values, rules) {
  const locked = new Set();
  const rule = rules[RULE_TYPES.LOCK_WHEN_CHECKED];
  if (!rule?.enabled || !rule?.targets) return locked;

  Object.entries(rule.targets).forEach(([triggerKey, lockedList]) => {
    if (values[triggerKey] === true) {
      lockedList.forEach((entry) => locked.add(getTargetKey(entry)));
    }
  });

  return locked;
}

function getPreserveOnReleaseKeys(rules) {
  const preserve = new Set();
  const rule = rules[RULE_TYPES.LOCK_WHEN_CHECKED];
  if (!rule?.targets) return preserve;

  Object.values(rule.targets).forEach((lockedList) => {
    (lockedList || []).forEach((entry) => {
      if (typeof entry === "object" && entry.preserveOnRelease) {
        preserve.add(entry.key);
      }
    });
  });

  return preserve;
}

function computeDisabledPermissions(values, rules) {
  const disabled = new Set();

  const disableRule = rules[RULE_TYPES.DISABLE_WHEN_CHECKED];
  if (disableRule?.enabled && disableRule?.targets) {
    Object.entries(disableRule.targets).forEach(
      ([triggerKey, disabledList]) => {
        if (values[triggerKey] === true) {
          disabledList.forEach((key) => disabled.add(key));
        }
      },
    );
  }

  const scRule = rules[RULE_TYPES.SPECIAL_CONDITION];
  if (scRule?.enabled && scRule?.targets) {
    Object.entries(scRule.targets).forEach(([targetKey, condition]) => {
      if (condition.requiresAny) {
        const satisfied = condition.requiresAny.some((key) => !!values[key]);
        if (!satisfied) {
          disabled.add(targetKey);
        }
      }
    });
  }

  return disabled;
}

export function computeParentClickChanges(
  parentKey,
  checked,
  section,
  behavior,
  ruleConfig,
) {
  const changes = { [parentKey]: checked };

  switch (behavior) {
    case CASCADE_BEHAVIORS.STRICT_CASCADE:
    case CASCADE_BEHAVIORS.NOTES_CASCADE:
      collectAllDescendants(section).forEach((id) => {
        changes[id] = checked;
      });
      break;

    case CASCADE_BEHAVIORS.PARTIAL_CASCADE:
      collectDirectChildren(section).forEach((id) => {
        changes[id] = checked;
      });
      break;

    case CASCADE_BEHAVIORS.THRESHOLD_HYSTERESIS:
      collectDirectChildren(section).forEach((id) => {
        changes[id] = checked;
      });
      (section.children || []).forEach((child) => {
        if (child.templates) {
          child.templates.forEach((t) => {
            changes[t.id] = false;
          });
        }
      });
      break;

    case CASCADE_BEHAVIORS.CHILD_SELECTS_PARENT:
      break;

    case CASCADE_BEHAVIORS.MERCURY_INBOX_FINANCIALS_RULES:
      if (!checked) {
        collectAllDescendants(section).forEach((id) => {
          changes[id] = false;
        });
      }
      break;

    case CASCADE_BEHAVIORS.LETTERS_RULE:
      collectAllDescendants(section).forEach((id) => {
        changes[id] = checked;
      });
      break;

    default:
      break;
  }

  return changes;
}

export function computeChildClickChanges(
  childId,
  checked,
  childConfig,
  behavior,
  parentKey,
  values,
) {
  const changes = { [childId]: checked };

  switch (behavior) {
    case CASCADE_BEHAVIORS.STRICT_CASCADE:
    case CASCADE_BEHAVIORS.NOTES_CASCADE:
      if (childConfig?.templates) {
        childConfig.templates.forEach((t) => {
          changes[t.id] = checked;
        });
      }
      if (childConfig?.type === "subgroup" && childConfig?.children) {
        childConfig.children.forEach((sc) => {
          changes[sc.id] = checked;
        });
      }
      break;

    case CASCADE_BEHAVIORS.PARTIAL_CASCADE:
      if (!checked) {
        if (childConfig?.templates) {
          childConfig.templates.forEach((t) => {
            changes[t.id] = false;
          });
        }

        if (childConfig?.type === "subgroup" && childConfig?.children) {
          childConfig.children.forEach((sc) => {
            changes[sc.id] = false;
          });
        }

        if (childConfig?.options) {
          childConfig.options.forEach((opt) => {
            changes[opt.id] = false;
          });
        }
      }
      break;

    case CASCADE_BEHAVIORS.THRESHOLD_HYSTERESIS:
      if (!checked) {
        if (childConfig?.templates && childConfig.templates.length > 0) {
          childConfig.templates.forEach((t) => {
            changes[t.id] = false;
          });
        }

        if (childConfig?.type === "subgroup" && childConfig?.children) {
          childConfig.children.forEach((sc) => {
            changes[sc.id] = false;
          });
        }

        if (childConfig?.options) {
          childConfig.options.forEach((opt) => {
            changes[opt.id] = false;
          });
        }
      }
      break;

    case CASCADE_BEHAVIORS.CHILD_SELECTS_PARENT:
      break;

    case CASCADE_BEHAVIORS.MERCURY_INBOX_FINANCIALS_RULES:
      if (checked) {
        changes[parentKey] = true;
      } else {
        if (childConfig?.options) {
          childConfig.options.forEach((opt) => {
            changes[opt.id] = false;
          });
        }
        if (childConfig?.templates) {
          childConfig.templates.forEach((t) => {
            changes[t.id] = false;
          });
        }
      }
      break;

    case CASCADE_BEHAVIORS.LETTERS_RULE:
      if (checked) {
        changes[parentKey] = true;
        if (childConfig?.options) {
          childConfig.options.forEach((opt) => {
            changes[opt.id] = true;
          });
        }
      } else {
        if (childConfig?.options) {
          childConfig.options.forEach((opt) => {
            changes[opt.id] = false;
          });
        }
      }
      break;

    default:
      break;
  }

  return changes;
}

export function computeGrandchildClickChanges(
  grandchildId,
  checked,
  parentChild,
  behavior,
  parentKey,
) {
  const changes = { [grandchildId]: checked };

  switch (behavior) {
    case CASCADE_BEHAVIORS.MERCURY_INBOX_FINANCIALS_RULES:
      if (checked) {
        changes[parentChild.id] = true;
        changes[parentKey] = true;
      }
      break;

    case CASCADE_BEHAVIORS.LETTERS_RULE:
      if (checked) {
        changes[parentChild.id] = true;
        changes[parentKey] = true;
      }
      break;

    default:
      break;
  }

  return changes;
}

function collectAllDescendants(section) {
  const ids = [];

  (section.children || []).forEach((child) => {
    ids.push(child.id);
    if (child.templates) child.templates.forEach((t) => ids.push(t.id));
    if (child.type === "subgroup" && child.children) {
      child.children.forEach((sc) => ids.push(sc.id));
    }
  });

  (section.sections || []).forEach((sub) => {
    (sub.permissions || []).forEach((perm) => {
      ids.push(perm.id);
      if (perm.options) perm.options.forEach((opt) => ids.push(opt.id));
    });
  });

  return ids;
}

function collectDirectChildren(section) {
  const ids = [];

  (section.children || []).forEach((child) => {
    ids.push(child.id);
  });

  (section.sections || []).forEach((sub) => {
    (sub.permissions || []).forEach((perm) => {
      ids.push(perm.id);
    });
  });

  return ids;
}
